import { AxiosResponse } from 'axios';
import { cloneDeep, isNumber } from 'lodash-es';
import { canUseFeature, fetchOrganizationFeatures } from 'resources/Organization/Features/FeaturesData';
import OrganizationApi from 'resources/Organization/OrganizationApi';
import { getQueryClient } from 'resources/ReactQuery/QueryClientProvider';
import { isAdminPanel } from 'utils/isAdminPanel';
import { isPresent } from 'utils/isPresent';

import { getOrganizationId } from 'components/AdminNew/Redux/Selectors';
import { AppDispatch } from 'components/Common/Data/StoreCreator';
import { OrganizationPayload } from 'components/Leads/hooks/useOrganization';
import { PAY_WALL_TRANSITION_DURATION } from 'components/PayWall/PayWallDialog';

import { showErrorDialog } from '../../Common/CommonActions';
import { fetchDataIfNeeded } from '../../Common/Data/DataFetcherSuperUtils';
import { WARNING_BAR } from '../../Common/NoticeBar/BannerBar';
import { SUCCESS_TEXT } from '../../Common/SuccessDialog';
import Utils from '../../Common/Utils';
import AnalyticsApi from '../../Services/Analytics/AnalyticsApi';
import { CLICK } from '../../Services/Analytics/TrackedEvents';
import { trackButtonClicked, trackPageViewedAsEvent } from '../../Services/Analytics/TrackEventsService';
import PayWallApi from '../api/PayWallApi';
import { PayWallPaymentSummary, PayWallPlan } from '../types';
import { processOrgFeatures } from '../utils/processOrgFeatures';

import { PayWallFullState, PayWallOrgFeature, PayWallState, PayWallTiers } from './Reducer.type';
import {
  getAdditionalUsers,
  getCurrentPlanAvailableFeatures,
  getOrgFeatures,
  getOriginalAdditionalUsers,
  getOriginalCurrentPlan,
  getSelectedDuration,
  getSelectedPlan,
  getUpdatePlanProps,
} from './Selectors';

type GetState = () => PayWallFullState;

const SMALLEST_TO_LARGEST_AVAILABLE_PLAN_TIERS = [
  PayWallTiers.STARTER,
  PayWallTiers.SMALL,
  PayWallTiers.MEDIUM,
  PayWallTiers.LARGE,
];
const UPSELL = 'Upsell';
const ENROLLED = 'Enrolled';
export const VEHICLE_TRACKING_FEATURE_NAME = 'vehicle_tracking';

export const AVAILABLE_PLANS = [PayWallTiers.STARTER, PayWallTiers.SMALL, PayWallTiers.MEDIUM];
export const MAX_NAME_LENGTH = 190;
export const WHEN_TO_APPLY = {
  NOW_ADDONS_ONLY: 'now_addons_only',
};
export const REQUIRED_MOST_POPULAR = 'most popular';
export const FEATURE_PROFIT_RHINO = 'profit_rhino';
export const FEATURE_SALES_MANAGEMENT = 'sales_management';

export const BILLING_INFO_UPDATED = 'BILLING_INFO_UPDATED';

export const UPDATE_ORGANIZATION_ADDRESS = 'UPDATE_ORGANIZATION_ADDRESS';
export const SHOW_CREDIT_CARD = 'SHOW_CREDIT_CARD';
export const PLAN_SELECTED = 'PLAN_SELECTED';
export const NAME_ON_CARD_UPDATED = 'NAME_ON_CARD_UPDATED';
export const DURATION_SELECTED = 'DURATION_SELECTED';
export const UPDATE_PLAN_PREVIEW = 'UPDATE_PLAN_PREVIEW';
export const DIALOG_PLAN_SELECTION = 'DIALOG_PLAN_SELECTION';
export const DIALOG_ADD_ONS = 'DIALOG_ADD_ONS';
export const DIALOG_PAYMENT_REVIEW = 'DIALOG_PAYMENT_REVIEW';
export const UPDATE_DIALOG_TYPE = 'UPDATE_DIALOG_TYPE';
export const UPDATE_SPINNER = 'UPDATE_SPINNER';
export const INITIALIZE_PAY_WALL_PLANS = 'INITIALIZE_PAY_WALL_PLANS';
export const HIDE_PAYWALL = 'HIDE_PAYWALL';
export const TOGGLE_PAYWALL_DIALOG_OPEN = 'TOGGLE_PAYWALL_DIALOG_OPEN';
export const ORG_FEATURES_UPDATED = 'ORG_FEATURES_UPDATED';
export const ADDITIONAL_USERS_UPDATED = 'ADDITIONAL_USERS_UPDATED';
export const CHANGE_BANNER_MESSAGE = 'CHANGE_BANNER_MESSAGE';
export const TOGGLE_UPSELL_CONTENT_DIALOG = 'TOGGLE_UPSELL_CONTENT_DIALOG';
export const PAY_WALL_CONFIRMATION_TEXT = 'PAY_WALL_CONFIRMATION_TEXT';
export const BILLING_ADDRESS_DIALOG_OPEN = 'BILLING_ADDRESS_DIALOG_OPEN';

export function openPayWall(planRequired: string, successCallback: () => void) {
  return {
    type: TOGGLE_PAYWALL_DIALOG_OPEN,
    status: true,
    required: planRequired,
    successCallback,
  };
}

export function openPayWallForFeature(feature: string, successCallback: () => void) {
  return {
    type: TOGGLE_PAYWALL_DIALOG_OPEN,
    status: true,
    feature,
    successCallback,
  };
}

/**
 * Makes sure modal hide transition finishes before reseting PayWall to initial state
 * (which would otherwise cause flash of unexpected content during transition)
 */
export function closePayWall() {
  return (dispatch: AppDispatch) => {
    dispatch({ type: HIDE_PAYWALL });
    setTimeout(() => {
      dispatch({ type: TOGGLE_PAYWALL_DIALOG_OPEN, status: false });
    }, PAY_WALL_TRANSITION_DURATION);
  };
}

export function isFeatureAvailableInCurrentPlan(feature: string) {
  return (dispatch: AppDispatch, getState: GetState) =>
    dispatch(fetchDataIfNeeded(fetchOrganizationFeatures)).then(() => canUseFeature(feature)(getState()));
}

function cheapestPlan(plans: PayWallPlan[]) {
  const plansByTier: Record<PayWallTiers, PayWallPlan> = plans.reduce(
    (planByTier, plan) => ({
      ...planByTier,
      [plan.tier]: plan,
    }),
    {} as Record<PayWallTiers, PayWallPlan>
  );

  return SMALLEST_TO_LARGEST_AVAILABLE_PLAN_TIERS.reduce<PayWallPlan>(
    (smallestPlan, tier) => smallestPlan || plansByTier[tier],
    null as unknown as PayWallPlan
  );
}

function getRequiredPlansByFeature(feature: string, availablePlans: PayWallPlan[]) {
  return availablePlans.filter((plan) => plan.features.some((planFeature) => planFeature === feature));
}

export function getCheapestPlanByFeature(feature: string, availablePlans: PayWallPlan[]) {
  return cheapestPlan(getRequiredPlansByFeature(feature, availablePlans));
}

export function fetchPlanData(version?: string) {
  return (dispatch: AppDispatch<PayWallFullState>, getState: GetState) => {
    const state = getState();
    const requiredPlan = state.payWall.required;
    const { feature } = state.payWall;
    const organizationId = getOrganizationId(state);

    dispatch({ type: UPDATE_SPINNER, spinnerState: true });

    return PayWallApi.fetchPlanData(version, state.payWall.variant, organizationId)
      .then(({ data }) => {
        const { plans } = data;

        let requiredTier = requiredPlan;

        if (feature) {
          const cheapestPlanByFeature = getCheapestPlanByFeature(feature, plans.available);

          requiredTier = cheapestPlanByFeature ? cheapestPlanByFeature.tier : requiredTier;
        }

        dispatch({ type: INITIALIZE_PAY_WALL_PLANS, data: plans, required: requiredTier });
      })
      .catch((error) => {
        if (error.response) {
          dispatch(showErrorDialog(Utils.parseErrorMessage(error)));
        }
      })
      .finally(() => {
        dispatch({ type: UPDATE_SPINNER, spinnerState: false });
        return new Promise<void>((resolve) => resolve());
      });
  };
}

export function updatePlan(dispatch: AppDispatch<PayWallFullState>, getState: GetState, shouldCharge: boolean) {
  dispatch({ type: UPDATE_SPINNER, spinnerState: true });

  const state = getState();
  const [planType, currentPlanId, orgFeatures, additionalUsers] = getUpdatePlanProps(state);

  const payload = {
    payment_plan: planType,
    billing_plan_id: currentPlanId,
    addon_features: orgFeatures,
    additional_users: additionalUsers,
    charge_after_plan_change: shouldCharge,
  };

  PayWallApi.updatePlan(payload)
    .then((response) => {
      onPaySuccess(dispatch, getState(), response);
      getQueryClient().invalidateQueries(['billingInfo']);
      getQueryClient().invalidateQueries(['subscriptionSummaryPreview']);
    })
    .catch((error) => {
      if (error.response) {
        dispatch(showErrorDialog(Utils.parseErrorMessage(error)));
      }
    })
    .finally(() => {
      dispatch({ type: UPDATE_SPINNER, spinnerState: false });
    });
}

export function updatePlanNoCharge() {
  return (dispatch: AppDispatch<PayWallFullState>, getState: GetState) => {
    updatePlan(dispatch, getState, false);
  };
}

export function updatePlanAndCharge() {
  return (dispatch: AppDispatch<PayWallFullState>, getState: GetState) => {
    updatePlan(dispatch, getState, true);
  };
}

type FetchUpdatePlanPreview = {
  planType?: string;
  planId?: number;
  addonFeatures?: PayWallOrgFeature[];
  additionalUsers?: number;
  onSuccess?: () => void;
};

export function fetchUpdatePlanPreview({
  planType,
  planId,
  addonFeatures,
  additionalUsers,
  onSuccess,
}: FetchUpdatePlanPreview = {}) {
  return (dispatch: AppDispatch, getState: GetState) => {
    const state = getState();
    const stateSelectedDuration = getSelectedDuration(state);
    const statePlanId = getSelectedPlan(state)?.id;
    const stateOrgFeatures = getOrgFeatures(state);
    const stateAdditionalUsers = getAdditionalUsers(state);
    const didChangeAdditionalUsers = isNumber(stateAdditionalUsers) || isNumber(additionalUsers);
    const additionalUsersForPreview = additionalUsers ?? stateAdditionalUsers;
    const availableFeatures = getCurrentPlanAvailableFeatures(state);
    const featuresToUse: PayWallOrgFeature[] = addonFeatures ?? processOrgFeatures(stateOrgFeatures, availableFeatures);

    const formData = {
      plan_type: planType ?? stateSelectedDuration,
      billing_plan_id: planId ?? statePlanId,
      addon_features: JSON.stringify(featuresToUse),
      ...(didChangeAdditionalUsers && { additional_users: additionalUsersForPreview }),
    };

    dispatch({ type: UPDATE_SPINNER, spinnerState: true });
    return PayWallApi.fetchUpdatePlanPreview(formData)
      .then((response) => {
        dispatch({ type: UPDATE_PLAN_PREVIEW, planSummaryPreview: response.data });
        dispatch({ type: ORG_FEATURES_UPDATED, updatedFeatures: response.data.additional_features });

        onSuccess?.();
      })
      .catch((error) => {
        if (error.response) {
          dispatch(showErrorDialog(error?.response?.data?.errors?.[0]));
        }
      })
      .finally(() => {
        dispatch({ type: UPDATE_SPINNER, spinnerState: false });
      });
  };
}

export function toggleOrgFeature(
  featureName: string,
  featurePrice: number | null,
  enabled: boolean,
  quantity?: number
) {
  return (dispatch: AppDispatch<PayWallFullState>, getState: GetState) => {
    const state = getState();
    const originalPayWall = state.payWall;
    const originalFeatures = originalPayWall.orgFeatures;

    let availableFeatures = getCurrentPlanAvailableFeatures(state);

    if (!availableFeatures.length) {
      availableFeatures = originalPayWall.availableFeatures;
    }

    const featureToToggle = availableFeatures.find((f) => f.name === featureName);
    const updatedFeatures = originalFeatures.filter((f) => f.name !== featureName);

    if (enabled && featureToToggle) {
      featureToToggle.price = featurePrice ?? 0;

      if (quantity) {
        featureToToggle.quantity = quantity;
      }

      updatedFeatures.push(featureToToggle as PayWallOrgFeature);
    }

    dispatch({
      type: ORG_FEATURES_UPDATED,
      updatedFeatures,
    });

    dispatch(fetchUpdatePlanPreview({ addonFeatures: updatedFeatures }));
  };
}

export function updateAdditionalUsers(newValue: number) {
  return (dispatch: AppDispatch<PayWallFullState>) => {
    dispatch({
      type: ADDITIONAL_USERS_UPDATED,
      additionalUsers: newValue,
    });

    dispatch(fetchUpdatePlanPreview({ additionalUsers: newValue }));
  };
}

const propagateUpdatedPlan = (
  response: AxiosResponse<PayWallPaymentSummary>,
  dispatch: AppDispatch<PayWallFullState>
) =>
  dispatch({
    type: BILLING_INFO_UPDATED,
    data: {
      planSummary: response.data.plan_summary,
      newPlanSummary: response.data.new_plan_summary,
      bills: response.data.bills,
    },
  });

function navigateToScreen(dialogType: string, planId: number, dispatch: AppDispatch<PayWallFullState>) {
  return dispatch(
    fetchUpdatePlanPreview({
      planId,
      onSuccess: () => {
        dispatch({ type: UPDATE_DIALOG_TYPE, dialogType });
      },
    })
  );
}

export function navigateToAddonsScreen(planId: number) {
  return (dispatch: AppDispatch<PayWallFullState>) => {
    dispatch(trackPageViewedAsEvent('add-on'));
    return navigateToScreen(DIALOG_ADD_ONS, planId, dispatch);
  };
}

export function navigateToReviewPlanChanges(planId: number) {
  return (dispatch: AppDispatch<PayWallFullState>) => {
    dispatch(trackPageViewedAsEvent('review and pay'));
    return navigateToScreen(DIALOG_PAYMENT_REVIEW, planId, dispatch);
  };
}

function trackGtm(planName: string, duration: string) {
  const { dataLayer } = window;

  try {
    dataLayer.push({
      event: 'transaction',
      plan: planName,
      frequency: duration,
    });
  } catch (e) {
    console.log('GTM error:', e);
  }
}

export const changeBannerMessage = (newMessage: Nullable<string>, bannerType = WARNING_BAR) => ({
  type: CHANGE_BANNER_MESSAGE,
  message: newMessage,
  bannerType,
});

export const organizationAddressHash = (addressData: Record<string, unknown>, unitNumber?: string | null) =>
  ({
    street: addressData.street,
    street_line_2: unitNumber,
    city: addressData.city,
    state: addressData.state,
    postal_code: addressData.postal_code || addressData.zip,
    country: addressData.country,
    latitude: addressData.latitude,
    longitude: addressData.longitude,
  } as OrganizationAddress);

export const updateOrganizationAddress = (
  addressData: OrganizationPayload | OrganizationAddress,
  unitNumber: string | undefined | null,
  dispatch: AppDispatch
) => {
  dispatch({
    type: UPDATE_ORGANIZATION_ADDRESS,
    organizationAddress: organizationAddressHash(addressData as unknown as Record<string, unknown>, unitNumber),
  });
};

export const fetchOrganizationAddress = (dispatch: AppDispatch) =>
  OrganizationApi.fetchOrganization().then((response) => {
    updateOrganizationAddress(response.data, response.data.street_line_2, dispatch);
  });

export type OrganizationAddress = {
  street: string;
  // eslint-disable-next-line camelcase
  street_line_2?: string | null;
  city: string;
  state: string;
  // eslint-disable-next-line camelcase
  postal_code?: string;
  zip?: string;
  country: string;
  latitude: number;
  longitude: number;
  // eslint-disable-next-line camelcase
  formatted_address: string;
};

export type AddressFields = Omit<OrganizationAddress, 'street_line_2'>;

const trackReferral = () => () => {
  const properties = {
    button_name: 'referral after plan change',
    pathname: window.location.pathname,
    url: window.location.href,
  };
  return AnalyticsApi.trackEvent(CLICK, properties);
};

const trackClicked = (buttonName: string) => (dispatch: AppDispatch) =>
  dispatch(trackButtonClicked(buttonName, { pathname: 'paywall confirmation modal' }));

const closeDialog = () => (dispatch: AppDispatch) => {
  window.parent.postMessage('closeDialog', '*');
  dispatch({ type: TOGGLE_PAYWALL_DIALOG_OPEN, status: false });
};

const openMobileSuccessDialog = () => (dispatch: AppDispatch, getState: GetState) => {
  const state = getState();

  dispatch({
    type: SUCCESS_TEXT,
    titleText: 'Your plan details have been successfully updated!',
    successText: 'Enjoying Housecall Pro? Tell a friend.',
    primaryButtonText: 'finish',
    successAction: () => {
      if (isPresent(state.payWall.onPaymentSuccess)) {
        dispatch(trackClicked('finish'));
        state.payWall.onPaymentSuccess();
      } else {
        window.location.reload();
      }
      dispatch(closeDialog());
    },
  });
};

const getShowVehicleTrackingModal = (state: PayWallState) => {
  const { orgFeatures, originalOrgFeatures } = state;

  const hasVehicleTracking = orgFeatures.some(
    (feature) => feature.name === VEHICLE_TRACKING_FEATURE_NAME && feature.quantifiable && feature.quantity
  );

  // Fast fail, it does not have vehicle tracking add-on in its current state so no need to bother
  if (!hasVehicleTracking) {
    return false;
  }

  const originalVehicleTracking = originalOrgFeatures.find((feature) => feature.name === VEHICLE_TRACKING_FEATURE_NAME);

  // Addon has been just added but plan used to not have it - show the modal
  if (!originalVehicleTracking && hasVehicleTracking) {
    return true;
  }

  const newVehicleTracking = orgFeatures.find((feature) => feature.name === VEHICLE_TRACKING_FEATURE_NAME);

  // It has vehicle tracking already enabled, so we want to show the modal only if additional devices have been added
  if (originalVehicleTracking && newVehicleTracking) {
    return (newVehicleTracking.quantity ?? 0) > (originalVehicleTracking.quantity ?? 0);
  }

  return false;
};

const openReferralDialog = () => (dispatch: AppDispatch<PayWallFullState>, getState: GetState) => {
  const { location } = window;
  const state = getState();

  dispatch({
    type: SUCCESS_TEXT,
    titleText: 'Your plan details have been successfully updated!',
    successText: 'Congrats! Share the love with your network.',
    successAction: () => {
      if (isPresent(state.payWall.onPaymentSuccess)) {
        dispatch(trackReferral()).then(() => {
          dispatch(closeDialog());
          location.href = '/pro/account/referrals?refer-a-friend=true&utm_content=plan-change';
        });
      } else {
        window.location.reload();
      }
      dispatch(closeDialog());
    },
    primaryButtonText: 'Refer a friend',
  });
};

const openSuccessDialog = () => (dispatch: AppDispatch, getState: GetState) => {
  const { location } = window;
  const state = getState();

  dispatch({
    type: SUCCESS_TEXT,
    titleText: 'Your plan details have been successfully updated!',
    successText: 'Enjoying Housecall Pro? Tell a friend.',
    successAction: () => {
      if (isPresent(state.payWall.onPaymentSuccess)) {
        dispatch(trackClicked('finish'));
        state.payWall.onPaymentSuccess();
      } else {
        window.location.reload();
      }
      dispatch(closeDialog());
    },
    primaryButtonText: 'finish',
    secondaryButtonText: 'Refer a friend',
    secondaryButtonAction: () => {
      dispatch(trackClicked('refer a friend')).then(() => {
        dispatch(closeDialog());
        location.href = '/pro/account/referrals?refer-a-friend=true';
      });
    },
  });
};

const openSuccessWithSubscriptionDialog = () => (dispatch: AppDispatch, getState: GetState) => {
  const state = getState();
  dispatch({
    type: SUCCESS_TEXT,
    successText:
      'You have successfully updated your Housecall Pro plan. You were previously paying for your subscription via the Apple App Store. Please be sure to cancel your old subscription within your Apple account. Please reach out to the Blue Chat Bubble if you have any questions.',
    successAction: () => {
      if (isPresent(state.payWall.onPaymentSuccess)) {
        dispatch(trackClicked('finish'));
        state.payWall.onPaymentSuccess();
      } else {
        window.location.reload();
      }
      dispatch(closeDialog());
    },
    primaryButtonText: 'Ok',
  });
};

const openAdditionalUsersSuccesDialog = (dispatch: AppDispatch, newAdditionalUsersCount: number) => {
  dispatch({
    type: SUCCESS_TEXT,
    titleText: 'Account updated',
    successText: `Total logins: ${newAdditionalUsersCount}`,
    successAction: () => dispatch(closeDialog()),
    primaryButtonText: 'close',
  });
};

const openVehicleTrackingSuccesDialog = () => (dispatch: AppDispatch) => {
  dispatch({
    type: SUCCESS_TEXT,
    titleText: 'Vehicle GPS tracking',
    successText:
      'Congratulations! Your plan has been successfully updated and vehicle GPS tracking is enabled.\n\n Provide your shipping address so we can send your GPS devices.',
    successAction: () => {
      window.location.href = '/pro/apps/details/vehicle-gps-tracking';
    },
    primaryButtonText: 'Shipping address',
  });
};

export const openUpsellDialog = () => ({ type: TOGGLE_UPSELL_CONTENT_DIALOG, status: true });

const openWebSuccessDialog =
  (response: AxiosResponse<PayWallPaymentSummary>, state: PayWallFullState) =>
  (dispatch: AppDispatch<PayWallFullState>) => {
    const isEnrollment = response.data.plan_change_type === ENROLLED;
    const isUpsell = response.data.plan_change_type === UPSELL;
    const needCancelSubscription = response.data.need_to_cancel_in_app_subscription;

    const showVehicleTrackingModal = getShowVehicleTrackingModal(state.payWall);

    if (showVehicleTrackingModal && !isAdminPanel) {
      dispatch(openVehicleTrackingSuccesDialog());
    } else if (needCancelSubscription) {
      dispatch(openSuccessWithSubscriptionDialog());
    } else if (isEnrollment || isUpsell) {
      dispatch(openReferralDialog());
    } else {
      dispatch(openSuccessDialog());
    }
  };

function onPaySuccess(
  dispatch: AppDispatch<PayWallFullState>,
  state: PayWallFullState,
  response: AxiosResponse<PayWallPaymentSummary>
) {
  // Closing the PayWall dialog also clears up its state entirely - we want to persist it's configuration state here
  // so that we can do some further tracking after the PayWall flow as well as fine-tune messaging
  // after the update is complete.
  const persistedState = cloneDeep<PayWallFullState>(state);

  propagateUpdatedPlan(response, dispatch);
  dispatch(closeDialog());

  if (state.mobile) {
    dispatch(openMobileSuccessDialog());
  } else {
    dispatch(openWebSuccessDialog(response, persistedState));
  }

  trackGtm(persistedState.payWall.selectedPlan.display_name!, persistedState.payWall.selectedDuration);
}

export const getAdditionalUsersPreview =
  (selectedAdditionalUsers: number) => (dispatch: AppDispatch<PayWallFullState>, getState: GetState) => {
    const state = getState();
    const originalAdditionalUsers = getOriginalAdditionalUsers(state) ?? 0;
    const originalPlan = getOriginalCurrentPlan(state);

    dispatch(
      fetchUpdatePlanPreview({
        additionalUsers: originalAdditionalUsers + selectedAdditionalUsers,
        planId: originalPlan?.id,
      })
    );
  };

export const updatePlanWithAdditionalUsers =
  (
    additionalUsersCount: number,
    totalUsersCount: number,
    successCallback: () => void,
    errorCallback: (message: string) => void
  ) =>
  (dispatch: AppDispatch<PayWallFullState>, getState: GetState) => {
    const {
      originalCurrentPlan: { id, plan_type: paymentPlan },
      orgFeatures,
      originalAdditionalUsers,
    } = getState().payWall;

    const newAdditionalUsersCount = additionalUsersCount + (originalAdditionalUsers ?? 0);

    dispatch({ type: UPDATE_SPINNER, spinnerState: true });

    const payload = {
      payment_plan: paymentPlan,
      billing_plan_id: id,
      addon_features: orgFeatures,
      additional_users: newAdditionalUsersCount,
      charge_after_plan_change: true,
    };

    return PayWallApi.updatePlan(payload)
      .then((response) => {
        propagateUpdatedPlan(response, dispatch);
        successCallback();
        openAdditionalUsersSuccesDialog(dispatch, totalUsersCount);
      })
      .catch((error) => {
        errorCallback(Utils.parseErrorMessage(error));
      })
      .finally(() => {
        dispatch({ type: UPDATE_SPINNER, spinnerState: false });
      });
  };

export const sendEmailReceipt = (billId: string, onSuccess: (data: AxiosResponse<unknown>) => void) => () =>
  PayWallApi.sendEmailReceipt(billId).then(onSuccess);
